
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFinance } from '../context/FinanceContext';
import { useAuth } from '../context/AuthContext';
import { Currency } from '../types';
import FinanceChatbot from './FinanceChatbot';
import { WealthyLogo } from './WealthyLogo';
import { 
  LayoutDashboard, 
  Receipt, 
  TrendingUp, 
  PieChart, 
  Settings,
  X,
  Menu,
  LogOut,
  Sun,
  Moon,
  Bot,
  Sparkles,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown
} from 'lucide-react';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { 
    currency, setCurrency, exchangeRates, updateExchangeRate, isDarkMode, setIsDarkMode,
    selectedYear, setSelectedYear, availableYears 
  } = useFinance();
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Expenses', href: '/expenses', icon: Receipt },
    { name: 'Investments', href: '/investments', icon: TrendingUp },
    { name: 'Balance Sheet', href: '/balance-sheet', icon: PieChart },
  ];

  return (
    <div className="flex h-screen overflow-hidden transition-colors duration-300 bg-[#f8fafc] text-slate-900">
      {/* Sidebar - Responsive */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-full sm:w-72 p-8 flex flex-col gap-10 transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 transition-all duration-300
        bg-white border-r border-slate-100
        ${isSidebarOpen ? 'translate-x-0 shadow-2xl shadow-slate-900/10' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between">
          <WealthyLogo size={24} showText={true} />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2.5 rounded-2xl border transition-all active:scale-95 flex items-center justify-center ${
                isDarkMode 
                  ? 'bg-slate-900 border-slate-700 text-amber-400 hover:bg-slate-800' 
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
              title={`Switch to ${isDarkMode ? 'Light' : 'Dark'} mode`}
              aria-label="Toggle light or dark theme"
            >
              {isDarkMode ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-blue-600" />}
            </button>
            <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 text-slate-300 hover:text-slate-900"><X size={28}/></button>
          </div>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto custom-scrollbar font-sans">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-4 px-5 py-5 rounded-[24px] transition-all group ${
                  isActive 
                    ? 'bg-slate-900 text-white shadow-xl shadow-slate-200' 
                    : 'text-slate-400 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <item.icon size={24} className={isActive ? 'text-blue-400' : 'text-slate-300 group-hover:text-slate-900'} />
                <span className="font-black text-base tracking-tight">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="space-y-6 pt-10 border-t pb-[env(safe-area-inset-bottom)] border-slate-50">
          <div className="flex p-1 rounded-2xl border min-h-[56px] items-center bg-slate-50 border-slate-100">
            {(Object.values(Currency) as Currency[]).map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={`flex-1 h-full py-2 text-[12px] font-black rounded-xl transition-all ${
                  currency === c
                    ? 'bg-white shadow-sm text-blue-600 scale-[1.05]'
                    : 'text-slate-400 hover:text-slate-700'
                }`}
              >
                {c}
              </button>
            ))}
            <button onClick={() => setIsConfigOpen(true)} className="p-3 transition-colors text-slate-300 hover:text-blue-600"><Settings size={16}/></button>
          </div>
          
          <div className="space-y-3 font-sans">
            <div className="p-4 rounded-3xl border flex items-center justify-between gap-3 bg-slate-50 border-slate-100">
               <div className="flex items-center gap-3 truncate">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-xs shadow-inner bg-blue-100 text-blue-600">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="truncate">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Signed In</p>
                    <p className="text-sm font-black truncate text-slate-850 text-slate-800">{user?.name}</p>
                  </div>
               </div>
               <button onClick={logout} className="p-3 transition-colors text-slate-300 hover:text-rose-500"><LogOut size={20}/></button>
            </div>
            <p className="text-[9px] font-semibold text-slate-400 text-center uppercase tracking-widest opacity-60">
              Wealth Architecture v1.0
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Global Navigation Bar */}
        <header className="border-b px-4 sm:px-8 py-3.5 flex justify-between items-center shrink-0 sticky top-0 z-40 pt-[env(safe-area-inset-top)] bg-white/85 backdrop-blur-md border-slate-100 transition-colors duration-300">
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Mobile hamburger */}
            <button 
              onClick={() => setIsSidebarOpen(true)} 
              className="lg:hidden p-2.5 rounded-2xl active:bg-slate-100 bg-slate-50 text-slate-600 border border-slate-100 hover:text-slate-900 transition-all active:scale-95"
              aria-label="Open Navigation Menu"
            >
              <Menu size={20}/>
            </button>
            
            {/* Mobile Brand / Desktop Breadcrumb */}
            <div className="flex items-center gap-2.5">
              <div className="lg:hidden flex items-center">
                <WealthyLogo size={18} showText={true} textClassName="text-base" />
              </div>

              {/* Desktop Page Title & Breadcrumb */}
              <div className="hidden lg:flex items-center gap-2.5">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-50 border border-slate-100">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Wealthy Architecture</span>
                </div>
                <span className="text-slate-300 font-bold">/</span>
                <span className="text-sm font-black text-slate-900 tracking-tight">
                  {navigation.find(n => n.href === location.pathname)?.name || 'Dashboard'}
                </span>
              </div>
            </div>
          </div>

          {/* Right side controls: Year Selector, Currency, Ask Wealthy & Global Theme Toggle */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Year-over-Year Global Selector */}
            <div className="flex items-center bg-slate-50 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700/80 p-1 rounded-2xl relative shadow-2xs">
              <button
                onClick={() => setSelectedYear(selectedYear - 1)}
                className="p-1.5 hover:bg-white dark:hover:bg-slate-700 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-xl transition-all active:scale-95 cursor-pointer"
                title={`Previous Year (${selectedYear - 1})`}
                aria-label="Previous Year"
              >
                <ChevronLeft size={14} />
              </button>

              <div className="relative">
                <button
                  onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-black text-slate-900 dark:text-white bg-white dark:bg-slate-900 shadow-xs rounded-xl hover:text-blue-600 dark:hover:text-blue-400 transition-all cursor-pointer font-['Sora',sans-serif]"
                >
                  <Calendar size={13} className="text-blue-600 dark:text-blue-400" />
                  <span>{selectedYear}</span>
                  <ChevronDown size={11} className={`text-slate-400 transition-transform duration-200 ${isYearDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isYearDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsYearDropdownOpen(false)} 
                    />
                    <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 p-2 z-50 min-w-[140px] space-y-1 animate-in fade-in zoom-in-95">
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-400 px-2.5 py-1">
                        Select Year
                      </div>
                      {availableYears.map(yr => (
                        <button
                          key={yr}
                          onClick={() => {
                            setSelectedYear(yr);
                            setIsYearDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                            selectedYear === yr 
                              ? 'bg-slate-900 dark:bg-blue-600 text-white font-black' 
                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-white'
                          }`}
                        >
                          <span>{yr}</span>
                          {yr === new Date().getFullYear() && (
                            <span className={`text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded font-black ${
                              selectedYear === yr ? 'bg-blue-600 dark:bg-blue-700 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}>
                              Current
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={() => setSelectedYear(selectedYear + 1)}
                className="p-1.5 hover:bg-white dark:hover:bg-slate-700 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-xl transition-all active:scale-95 cursor-pointer"
                title={`Next Year (${selectedYear + 1})`}
                aria-label="Next Year"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            {/* Currency quick switcher */}
            <div className="hidden sm:flex items-center bg-slate-50 border border-slate-100 p-1 rounded-2xl">
              {(Object.values(Currency) as Currency[]).map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1.5 text-[11px] font-black rounded-xl transition-all ${
                    currency === c
                      ? 'bg-white shadow-xs text-blue-600 scale-[1.03]'
                      : 'text-slate-400 hover:text-slate-700'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            {/* ASK WEALTHY AI TRIGGER */}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-wealthy-chat'))}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black text-xs shadow-md shadow-blue-500/20 hover:scale-[1.03] active:scale-95 transition-all cursor-pointer"
              title="Open Ask Wealthy AI Coach"
              aria-label="Open Ask Wealthy AI Chatbot"
            >
              <Bot size={16} className="animate-pulse" />
              <span className="text-xs font-black tracking-tight">Ask Wealthy</span>
              <Sparkles size={13} className="text-yellow-300 hidden xs:inline" />
            </button>

            {/* GLOBAL THEME TOGGLE BUTTON */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`
                relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-2xl border transition-all duration-300 active:scale-95 group
                ${isDarkMode 
                  ? 'bg-slate-900/90 hover:bg-slate-800 border-slate-700 text-amber-400 shadow-sm' 
                  : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700 shadow-xs'}
              `}
              title={`Switch to ${isDarkMode ? 'Light' : 'Dark'} mode`}
              aria-label="Toggle light or dark theme"
            >
              <div className={`p-1 rounded-lg transition-transform duration-300 ${isDarkMode ? 'rotate-180 bg-amber-400/10' : 'bg-blue-50 text-blue-600'}`}>
                {isDarkMode ? <Sun size={17} className="text-amber-400" /> : <Moon size={17} className="text-blue-600" />}
              </div>
              <div className="flex flex-col text-left">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 leading-none">Theme</span>
                <span className="text-xs font-black tracking-tight leading-tight hidden xs:inline sm:inline">
                  {isDarkMode ? 'Dark' : 'Light'}
                </span>
              </div>
              {/* Visual status dot */}
              <span className={`w-2 h-2 rounded-full ml-1 transition-colors ${isDarkMode ? 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'bg-blue-500'}`} />
            </button>
          </div>
        </header>

        <main className={`flex-1 overflow-y-auto p-4 sm:p-12 custom-scrollbar transition-colors duration-300 bg-[#f8fafc] ${isDarkMode ? 'dark' : ''}`}>
          <div className="max-w-6xl mx-auto w-full mobile-safe-bottom">
            {children}
          </div>
        </main>
      </div>

      {/* Currency Modal */}
      {isConfigOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[40px] max-w-sm w-full p-8 sm:p-12 shadow-2xl relative border border-slate-50">
            <button onClick={() => setIsConfigOpen(false)} className="absolute top-6 right-6 text-slate-300 p-2 hover:text-slate-900 transition-colors"><X size={28} /></button>
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter mb-10">Conversions</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">EUR Rate (vs USD)</label>
                <input type="number" step="0.001" value={exchangeRates[Currency.EUR]} onChange={(e) => updateExchangeRate(Currency.EUR, parseFloat(e.target.value))} className="w-full p-5 bg-slate-50 rounded-3xl text-base font-black outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">AED Rate (vs USD)</label>
                <input type="number" step="0.001" value={exchangeRates[Currency.AED]} onChange={(e) => updateExchangeRate(Currency.AED, parseFloat(e.target.value))} className="w-full p-5 bg-slate-50 rounded-3xl text-base font-black outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
            </div>
            <button onClick={() => setIsConfigOpen(false)} className="w-full mt-10 bg-slate-900 text-white font-black py-5 rounded-3xl shadow-xl shadow-slate-100 hover:scale-[0.98] active:scale-[0.95] transition-all uppercase tracking-widest text-sm">Confirm Updates</button>
          </div>
        </div>
      )}

      {/* Global AI Wealth Chatbot */}
      <FinanceChatbot />
    </div>
  );
};

export default Layout;
